import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Section } from '../types';
import SortableItem from './SortableItem';
import { DEFAULT_FRONT_MATTER } from '../hooks/useBookLoader';

interface SectionSidebarProps {
  bookTitle: string;
  sections: Section[];
  activeSectionId: string;
  onSectionSelect: (id: string) => void;
  onSectionDelete: (id: string) => void;
  onSectionRename: (id: string, newTitle: string) => void;
  onAddChapter: () => void;
  onAddFrontMatter: (id: string, title: string) => void;
  onAddEndMatter: () => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SectionSidebar: React.FC<SectionSidebarProps> = ({
  bookTitle, sections, activeSectionId,
  onSectionSelect, onSectionDelete, onSectionRename, onAddChapter, onAddFrontMatter, onAddEndMatter, onDragEnd
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [showFrontMatterMenu, setShowFrontMatterMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const frontMatterSections = sections.filter(s => s.type === 'front-matter');
  const chapterSections = sections.filter(s => s.type === 'chapter');
  const endMatterSections = sections.filter(s => s.type === 'end-matter');

  const existingFrontMatterIds = new Set(frontMatterSections.map(s => s.id));
  const missingFrontMatter = DEFAULT_FRONT_MATTER.filter(fm => !existingFrontMatterIds.has(fm.id));

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowFrontMatterMenu(false);
      }
    };
    if (showFrontMatterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFrontMatterMenu]);

  return (
    <div className="notion-sidebar">
      <div className="sidebar-header">
        <h2><span className="sidebar-hash">#</span>{bookTitle}</h2>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="sidebar-sections">
          {/* Front Matter */}
          <div className="sidebar-category">
            <div className="category-header sidebar-category-active" ref={menuRef}>
              <h3>Front Matter</h3>
              <div style={{ position: 'relative' }}>
                <button
                  className="add-section-btn"
                  onClick={() => missingFrontMatter.length > 0 && setShowFrontMatterMenu(!showFrontMatterMenu)}
                  style={missingFrontMatter.length === 0 ? { opacity: 0.4, cursor: 'default' } : {}}
                  title={missingFrontMatter.length === 0 ? 'All front matter sections added' : 'Add front matter section'}
                ><Plus size={12} strokeWidth={2} /></button>
                {showFrontMatterMenu && missingFrontMatter.length > 0 && (
                  <div className="front-matter-dropdown">
                    {missingFrontMatter.map(fm => (
                      <button
                        key={fm.id}
                        className="front-matter-dropdown-item"
                        onClick={() => {
                          onAddFrontMatter(fm.id, fm.title);
                          setShowFrontMatterMenu(false);
                        }}
                      >
                        {fm.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <SortableContext items={frontMatterSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {frontMatterSections.map((section) => (
                <SortableItem
                  key={section.id}
                  id={section.id}
                  section={section}
                  isActive={activeSectionId === section.id}
                  onSelect={() => onSectionSelect(section.id)}
                  onDelete={() => onSectionDelete(section.id)}
                  onRename={(newTitle) => onSectionRename(section.id, newTitle)}
                />
              ))}
            </SortableContext>
          </div>

          {/* Chapters */}
          <div className="sidebar-category">
            <div className="category-header">
              <h3>Chapters</h3>
              <button className="add-section-btn" onClick={onAddChapter} title="Add chapter"><Plus size={12} strokeWidth={2} /></button>
            </div>
            <SortableContext items={chapterSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {chapterSections.map((section) => (
                <SortableItem
                  key={section.id}
                  id={section.id}
                  section={section}
                  isActive={activeSectionId === section.id}
                  onSelect={() => onSectionSelect(section.id)}
                  onDelete={() => onSectionDelete(section.id)}
                  onRename={(newTitle) => onSectionRename(section.id, newTitle)}
                />
              ))}
            </SortableContext>
          </div>

          {/* End Matter */}
          <div className="sidebar-category">
            <div className="category-header">
              <h3>End Matter</h3>
              <button className="add-section-btn" onClick={onAddEndMatter} title="Add end matter section"><Plus size={12} strokeWidth={2} /></button>
            </div>
            <SortableContext items={endMatterSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {endMatterSections.map((section) => (
                <SortableItem
                  key={section.id}
                  id={section.id}
                  section={section}
                  isActive={activeSectionId === section.id}
                  onSelect={() => onSectionSelect(section.id)}
                  onDelete={() => onSectionDelete(section.id)}
                  onRename={(newTitle) => onSectionRename(section.id, newTitle)}
                />
              ))}
            </SortableContext>
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default SectionSidebar;
